import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import {
  addDays,
  formatLongDate,
  formatShortDate,
  formatTimeRange,
  minutesToClock,
  weekdayName,
  type ISODate,
} from '@app/shared'

import { SkeletonRows } from '../components/Card'
import {
  PILES,
  PILE_LABEL,
  PILE_WORD,
  REASON_CHIPS,
  agoFrom,
  carryOverLabel,
  deadlineLabel,
  inboxLabel,
  provenance,
  senderDomain,
  senderLabel,
  snoozePresets,
  sortedAsOf,
} from '../components/day/model'
import {
  useCalendarRange,
  useClassOccurrences,
  useClock,
  useCreateMailRule,
  useMailTriage,
  useReminders,
  useRunMailTriage,
  useSetMailState,
  useSetMailVerdict,
  useSyncConnector,
} from '../lib/data'
import type { MailTriage, MailTriageItem, MailVerdict } from '../lib/data/types'

// ============================================================================
// THE DAY PAGE — FUNCTIONAL PROTOTYPE (owner, 2026-10-05: "functional, not pretty").
//
// THE PHONE'S DAY PAGE, still (Lane 4a, 2026-10-06). The designed desktop page (routes/Day.tsx,
// docs/design/handoff/day-page/) replaced this on the desktop only; the owner is redesigning the
// phone separately, so the phone shell (mobile/MobileApp.tsx) keeps mounting THIS at `/day`, reached
// from the phone's Today. It does the job with the app's existing tokens: sorted mail from three
// inboxes with grading, due work, and today's classes.
//
// Every mail verdict carries its provenance, counts are the Worker's true counts, the folded piles
// are folded and never hidden, and a failed read says so in its own words.
// ============================================================================

const MOVE_CHIP_MS = 6_000

function errorText(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback
}

export function DayPrototype() {
  const { data: clock } = useClock()

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-page">
      <div className="mx-auto flex w-full max-w-[760px] flex-col gap-5 px-4 pb-10 pt-4">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <Link to="/" className="text-[12px] font-medium text-ink-3 underline underline-offset-2">
            ← Today
          </Link>
          <p className="text-[11.5px] leading-[1.4] text-microcopy">
            Prototype — the designed page replaces this.
          </p>
        </div>
        {clock ? (
          <>
            <MailBlock today={clock.date} nowMinutes={clock.minutesIntoDay} />
            <DueBlock today={clock.date} />
            <TodayBlock today={clock.date} />
          </>
        ) : (
          <SkeletonRows rows={4} />
        )}
      </div>
    </div>
  )
}

// ── Small parts ─────────────────────────────────────────────────────────────

function Btn({
  children,
  onClick,
  disabled,
  pressed,
  expanded,
  tone = 'plain',
}: {
  children: ReactNode
  onClick: () => void
  disabled?: boolean
  pressed?: boolean
  expanded?: boolean
  tone?: 'plain' | 'dark'
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={pressed}
      aria-expanded={expanded}
      className={clsx(
        'rounded-[6px] border px-[9px] py-[5px] text-[11.5px] font-medium leading-none disabled:opacity-50',
        tone === 'dark'
          ? 'border-ink bg-ink text-surface hover:opacity-90'
          : 'border-line bg-surface text-ink-2 hover:bg-surface-2',
      )}
    >
      {children}
    </button>
  )
}

function Badge({ children, tone = 'plain' }: { children: ReactNode; tone?: 'plain' | 'danger' }) {
  return (
    <span
      className={clsx(
        'rounded-[4px] border px-[6px] py-[2px] text-[10.5px] font-medium leading-none',
        tone === 'danger'
          ? 'border-danger-line bg-danger-fill text-danger'
          : 'border-line bg-surface-2 text-ink-3',
      )}
    >
      {children}
    </span>
  )
}

function BlockTitle({ children }: { children: ReactNode }) {
  return <h2 className="text-[13px] font-semibold leading-none text-ink">{children}</h2>
}

function Panel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <section className={clsx('flex flex-col gap-3 rounded-card border border-line bg-surface px-4 py-[14px]', className)}>
      {children}
    </section>
  )
}

function Muted({ children }: { children: ReactNode }) {
  return <p className="text-[12px] leading-[1.45] text-microcopy text-pretty">{children}</p>
}

// ── Mail ────────────────────────────────────────────────────────────────────

function MailBlock({ today, nowMinutes }: { today: ISODate; nowMinutes: number }) {
  const triage = useMailTriage()

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-col gap-2">
        <h1 className="text-[20px] font-semibold leading-[1.2] text-ink">{formatLongDate(today)}</h1>
        {triage.data && <Freshness triage={triage.data} today={today} nowMinutes={nowMinutes} />}
      </header>

      {triage.isPending && <SkeletonRows rows={5} />}

      {triage.error && (
        <Panel className="border-danger-line">
          <p className="text-[12px] leading-[1.45] text-danger text-pretty">
            {errorText(triage.error, 'The sorted mail could not be loaded.')}
          </p>
          <div>
            <Btn onClick={() => void triage.refetch()} disabled={triage.isFetching}>
              {triage.isFetching ? 'Retrying…' : 'Retry'}
            </Btn>
          </div>
        </Panel>
      )}

      {triage.data && triage.data.accounts.length === 0 && (
        <Panel>
          <Muted>No Google account is connected, so there is no mail to sort.</Muted>
          <Link
            to="/settings/connections"
            className="text-[12px] font-medium text-ink-2 underline underline-offset-2"
          >
            Connect one in Settings → Connections
          </Link>
        </Panel>
      )}

      {triage.data && triage.data.accounts.length > 0 && (
        <Piles triage={triage.data} today={today} nowMinutes={nowMinutes} />
      )}
    </div>
  )
}

/** "sorted as of 8:32am", one line per inbox, and the two buttons that make the page current. */
function Freshness({ triage, today, nowMinutes }: { triage: MailTriage; today: ISODate; nowMinutes: number }) {
  const sync = useSyncConnector()
  const run = useRunMailTriage()
  const [busy, setBusy] = useState<'sync' | 'sort' | null>(null)
  const [note, setNote] = useState<string | null>(null)

  const runLine = (r: { sorted: number; failed: number; remaining: number }) =>
    `Sorted ${r.sorted}${r.failed ? ` · ${r.failed} failed` : ''}${r.remaining ? ` · ${r.remaining} still waiting` : ''}.`

  const syncNow = async () => {
    setBusy('sync')
    setNote(null)
    const notes: string[] = []
    try {
      await sync.mutateAsync('gmail')
    } catch (e) {
      notes.push(errorText(e, 'The Gmail sync did not go through.'))
    }
    try {
      notes.push(runLine(await run.mutateAsync(undefined)))
    } catch (e) {
      notes.push(errorText(e, 'Sorting did not go through.'))
    }
    setNote(notes.join(' '))
    setBusy(null)
  }

  const sortNow = async () => {
    setBusy('sort')
    setNote(null)
    try {
      setNote(runLine(await run.mutateAsync(undefined)))
    } catch (e) {
      setNote(errorText(e, 'Sorting did not go through.'))
    }
    setBusy(null)
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="text-[12px] leading-[1.4] text-ink-3">{sortedAsOf(triage.generatedAt, today)}</p>
      <ul className="flex flex-col gap-[3px]">
        {triage.accounts.map((a) => (
          <li key={a.id} className="text-[11.5px] leading-[1.4] text-pretty">
            <span className="text-ink-2">{a.address}</span>
            {a.lastError ? (
              <span className="text-danger"> · {a.lastError}</span>
            ) : (
              <span className="text-microcopy">
                {' · '}
                {a.lastSyncAt ? `synced ${agoFrom(a.lastSyncAt, today, nowMinutes)}` : 'not synced yet'}
              </span>
            )}
          </li>
        ))}
      </ul>
      {triage.accounts.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <Btn onClick={() => void syncNow()} disabled={busy !== null}>
            {busy === 'sync' ? 'Syncing…' : 'Sync now'}
          </Btn>
          {triage.counts.unsorted > 0 && (
            <Btn onClick={() => void sortNow()} disabled={busy !== null}>
              {busy === 'sort' ? 'Sorting…' : 'Sort unsorted'}
            </Btn>
          )}
        </div>
      )}
      {note && (
        <p role="status" className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">
          {note}
        </p>
      )}
    </div>
  )
}

function Piles({ triage, today, nowMinutes }: { triage: MailTriage; today: ISODate; nowMinutes: number }) {
  const { counts, groups } = triage
  const [openCanWait, setOpenCanWait] = useState(false)
  const [openNoise, setOpenNoise] = useState(false)

  // THE REASON CHIPS OUTLIVE THE ROW'S POSITION. A move re-files the message under another pile,
  // which remounts its row there; the "why?" chips follow it by id from here, for ~6 s.
  const [recentMove, setRecentMove] = useState<{ id: string; verdict: MailVerdict } | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
  }, [])
  const onMoved = (id: string, verdict: MailVerdict) => {
    if (timer.current) clearTimeout(timer.current)
    setRecentMove({ id, verdict })
    timer.current = setTimeout(() => setRecentMove(null), MOVE_CHIP_MS)
  }
  const clearMove = () => {
    if (timer.current) clearTimeout(timer.current)
    setRecentMove(null)
  }

  const list = (items: MailTriageItem[]) => (
    <ul className="flex flex-col divide-y divide-line">
      {items.map((item) => (
        <MailRow
          key={item.id}
          item={item}
          today={today}
          nowMinutes={nowMinutes}
          recentMove={recentMove?.id === item.id ? recentMove.verdict : null}
          onMoved={onMoved}
          onReasonDone={clearMove}
        />
      ))}
    </ul>
  )

  return (
    <div className="flex flex-col gap-4">
      <Pile title={`Needs you today · ${counts.needsYouToday}`}>
        {counts.needsYouToday === 0 && groups.needsYouToday.length === 0 ? (
          <Muted>Nothing needs you today.</Muted>
        ) : (
          list(groups.needsYouToday)
        )}
      </Pile>

      <Pile title={`Important · ${counts.important}`}>
        {groups.important.length === 0 ? <Muted>Nothing important waiting.</Muted> : list(groups.important)}
      </Pile>

      {counts.unsorted > 0 && (
        <Pile title={`Not sorted yet · ${counts.unsorted}`}>{list(groups.unsorted)}</Pile>
      )}

      <Pile
        title={`Can wait · ${counts.canWait}`}
        open={openCanWait}
        onToggle={() => setOpenCanWait((v) => !v)}
      >
        {groups.canWait.length === 0 ? <Muted>Nothing here.</Muted> : list(groups.canWait)}
      </Pile>

      <Pile
        title={openNoise ? `Noise · ${counts.noise}` : `Noise · ${counts.noise} folded`}
        open={openNoise}
        onToggle={() => setOpenNoise((v) => !v)}
      >
        {groups.noise.length === 0 ? <Muted>Nothing here.</Muted> : list(groups.noise)}
      </Pile>

      {counts.expiredCodes > 0 && (
        <Muted>
          {counts.expiredCodes} expired sign-in {counts.expiredCodes === 1 ? 'code' : 'codes'} hidden
        </Muted>
      )}
    </div>
  )
}

/** A pile. With `onToggle` it folds; the heading is then the button (keyboard and screen reader). */
function Pile({
  title,
  open = true,
  onToggle,
  children,
}: {
  title: string
  open?: boolean
  onToggle?: () => void
  children: ReactNode
}) {
  return (
    <Panel>
      {onToggle ? (
        <h2 className="text-[13px] font-semibold leading-none text-ink">
          <button type="button" onClick={onToggle} aria-expanded={open} className="flex items-center gap-2 text-left">
            <span aria-hidden className="w-3 text-ink-3">{open ? '▾' : '▸'}</span>
            {title}
          </button>
        </h2>
      ) : (
        <BlockTitle>{title}</BlockTitle>
      )}
      {open && children}
    </Panel>
  )
}

type Menu = 'snooze' | 'rule' | null

function MailRow({
  item,
  today,
  nowMinutes,
  recentMove,
  onMoved,
  onReasonDone,
}: {
  item: MailTriageItem
  today: ISODate
  nowMinutes: number
  recentMove: MailVerdict | null
  onMoved: (id: string, verdict: MailVerdict) => void
  onReasonDone: () => void
}) {
  const [menu, setMenu] = useState<Menu>(null)
  const [ruleNote, setRuleNote] = useState<string | null>(null)
  const state = useSetMailState()
  const verdict = useSetMailVerdict()
  const rule = useCreateMailRule()

  const busy = state.isPending || verdict.isPending || rule.isPending
  const sender = senderLabel(item)
  const carry = carryOverLabel(item.ageDays)
  const prov = provenance(item)
  const error = state.error ?? verdict.error ?? rule.error
  const toggle = (m: Exclude<Menu, null>) => setMenu((cur) => (cur === m ? null : m))

  const saveRule = (matchType: 'address' | 'domain', matchValue: string) => {
    if (!item.verdict) return
    const pile = item.verdict
    rule.mutate(
      { matchType, matchValue, verdict: pile },
      {
        onSuccess: () => {
          setMenu(null)
          setRuleNote(`Rule saved: always ${PILE_WORD[pile]} from ${matchValue}.`)
        },
      },
    )
  }

  return (
    <li className="flex flex-col gap-[6px] py-3 first:pt-0 last:pb-0" data-mail-id={item.id}>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="text-[12.5px] font-semibold leading-[1.3] text-ink">{sender}</span>
        <span className="text-[11px] leading-[1.3] text-microcopy">{inboxLabel(item.accountAddress)}</span>
        {item.security && <Badge tone="danger">security</Badge>}
        {item.deadline && <Badge>{deadlineLabel(item.deadline)}</Badge>}
        {carry && <Badge>{carry}</Badge>}
      </div>

      <a
        href={item.url}
        target="_blank"
        rel="noreferrer"
        className="text-[13.5px] font-medium leading-[1.35] text-ink underline-offset-2 hover:underline text-pretty"
      >
        {item.subject || '(no subject)'}
      </a>

      {item.reason && <p className="text-[12px] leading-[1.45] text-ink-2 text-pretty">{item.reason}</p>}
      {prov && <p className="text-[10.5px] leading-[1.4] text-microcopy">{prov}</p>}

      <div className="flex flex-wrap items-center gap-[6px] pt-[2px]">
        <Btn disabled={busy} onClick={() => state.mutate({ id: item.id, state: 'done' })}>
          Done
        </Btn>
        <Btn disabled={busy} expanded={menu === 'snooze'} onClick={() => toggle('snooze')}>
          Snooze ▾
        </Btn>
        <Btn disabled={busy} onClick={() => state.mutate({ id: item.id, state: 'dismissed' })}>
          Dismiss
        </Btn>
        <span className="pl-1 text-[11px] text-microcopy">Move to</span>
        {PILES.filter((p) => p !== item.verdict).map((p) => (
          <Btn
            key={p}
            disabled={busy}
            onClick={() =>
              verdict.mutate({ id: item.id, verdict: p }, { onSuccess: () => onMoved(item.id, p) })
            }
          >
            {PILE_LABEL[p]}
          </Btn>
        ))}
        {item.verdict && (
          <Btn disabled={busy} expanded={menu === 'rule'} onClick={() => toggle('rule')}>
            Always {PILE_WORD[item.verdict]} from {sender} ▾
          </Btn>
        )}
      </div>

      {menu === 'snooze' && (
        <div className="flex flex-wrap items-center gap-[6px]" role="group" aria-label="Snooze until">
          {snoozePresets(today, nowMinutes).map((p) => (
            <Btn
              key={p.label}
              disabled={busy}
              onClick={() =>
                state.mutate(
                  { id: item.id, state: 'snoozed', snoozedUntil: p.until },
                  { onSuccess: () => setMenu(null) },
                )
              }
            >
              {p.label}
            </Btn>
          ))}
        </div>
      )}

      {menu === 'rule' && item.verdict && (
        <div className="flex flex-wrap items-center gap-[6px]" role="group" aria-label="Rule for this sender">
          <Btn disabled={busy} onClick={() => saveRule('address', item.from.address.toLowerCase())}>
            this address ({item.from.address})
          </Btn>
          <Btn disabled={busy} onClick={() => saveRule('domain', senderDomain(item.from.address))}>
            this domain ({senderDomain(item.from.address)})
          </Btn>
        </div>
      )}

      {recentMove && (
        <div className="flex flex-wrap items-center gap-[6px]" role="group" aria-label="Why did you move it?">
          <span className="text-[11px] text-microcopy">Moved to {PILE_WORD[recentMove]}. Why? (optional)</span>
          {REASON_CHIPS.map((c) => (
            <Btn
              key={c.reason}
              disabled={busy}
              onClick={() =>
                verdict.mutate(
                  { id: item.id, verdict: recentMove, reason: c.reason },
                  { onSuccess: onReasonDone },
                )
              }
            >
              {c.label}
            </Btn>
          ))}
        </div>
      )}

      {ruleNote && !error && (
        <p role="status" className="text-[11px] leading-[1.4] text-success">
          {ruleNote}
        </p>
      )}
      {error && (
        <p className="text-[11px] leading-[1.4] text-danger text-pretty">
          {errorText(error, 'That did not go through. Try again.')}
        </p>
      )}
    </li>
  )
}

// ── Due (read-only) ─────────────────────────────────────────────────────────

function dueWhen(date: ISODate, today: ISODate, minutes: number | null): string {
  const day = date === today ? 'today' : `${weekdayName(date, true)} ${formatShortDate(date)}`
  return minutes === null ? `${day} · no time given` : `${day} ${minutesToClock(minutes)}`
}

function DueBlock({ today }: { today: ISODate }) {
  const range = useCalendarRange({ from: today, to: addDays(today, 7) })
  const rows = (range.data?.dueItems ?? [])
    .filter((d) => d.status === 'upcoming' || d.status === 'missing')
    .sort((a, b) => (a.date === b.date ? (a.dueMinutes ?? 1440) - (b.dueMinutes ?? 1440) : a.date < b.date ? -1 : 1))
  const dueToday = rows.filter((d) => d.date === today)
  const later = rows.filter((d) => d.date > today)

  const line = (d: (typeof rows)[number]) => (
    <li key={d.id} className="text-[12.5px] leading-[1.45] text-ink-2 text-pretty">
      <span className="font-medium text-ink">{d.title}</span>
      {d.courseCode && <span> · {d.courseCode}</span>}
      <span className="text-microcopy"> · {dueWhen(d.date, today, d.dueMinutes)}</span>
    </li>
  )

  return (
    <Panel>
      <BlockTitle>Due</BlockTitle>
      {range.isPending && <SkeletonRows rows={3} />}
      {range.error && <Muted>{errorText(range.error, 'Due work could not be loaded.')}</Muted>}
      {range.data && (
        <>
          <p className="text-[11px] font-semibold leading-none text-ink-3">Today</p>
          {dueToday.length === 0 ? <Muted>Nothing due today.</Muted> : <ul className="flex flex-col gap-1">{dueToday.map(line)}</ul>}
          <p className="text-[11px] font-semibold leading-none text-ink-3">Next 7 days</p>
          {later.length === 0 ? <Muted>Nothing due in the next 7 days.</Muted> : <ul className="flex flex-col gap-1">{later.map(line)}</ul>}
        </>
      )}
    </Panel>
  )
}

// ── Today (read-only) ───────────────────────────────────────────────────────

const KIND_LABEL: Record<string, string> = { lecture: 'Lecture', discussion: 'Discussion', lab: 'Lab' }

function TodayBlock({ today }: { today: ISODate }) {
  const classes = useClassOccurrences(today)
  const range = useCalendarRange({ from: today, to: addDays(today, 7) })
  const reminders = useReminders()

  // Canvas classes come from the class schedule above; the calendar adds what is NOT a class.
  const blocks = (range.data?.blocks ?? []).filter(
    (b) => b.date === today && b.source !== 'canvas' && b.state === 'accepted' && b.status !== 'cancelled',
  )
  const allDay = (range.data?.allDayEvents ?? []).filter(
    (e) => e.date <= today && today <= e.endDate && e.status !== 'cancelled',
  )
  const timed = [
    ...(classes.data ?? []).map((c) => ({
      key: `class-${c.courseId}-${c.startMinutes}`,
      start: c.startMinutes,
      text: `${formatTimeRange(c.startMinutes, c.endMinutes)} · ${c.courseCode} ${KIND_LABEL[c.kind] ?? c.kind}${c.location ? ` · ${c.location}` : ''}`,
    })),
    ...blocks.map((b) => ({
      key: `block-${b.id}`,
      start: b.startMinutes,
      text: `${formatTimeRange(b.startMinutes, b.endMinutes)} · ${b.title}${b.location ? ` · ${b.location}` : ''}`,
    })),
  ].sort((a, b) => a.start - b.start)

  const openReminders = (reminders.data ?? []).filter((t) => !t.done).length

  return (
    <Panel>
      <BlockTitle>Today</BlockTitle>
      {(classes.isPending || range.isPending) && <SkeletonRows rows={3} />}
      {classes.error && <Muted>{errorText(classes.error, 'Classes could not be loaded.')}</Muted>}
      {range.error && <Muted>{errorText(range.error, 'The calendar could not be loaded.')}</Muted>}
      {classes.data && range.data && timed.length === 0 && allDay.length === 0 && (
        <Muted>No classes or events today.</Muted>
      )}
      {(timed.length > 0 || allDay.length > 0) && (
        <ul className="flex flex-col gap-1">
          {allDay.map((e) => (
            <li key={e.id} className="text-[12.5px] leading-[1.45] text-ink-2">
              All day · {e.title}
            </li>
          ))}
          {timed.map((t) => (
            <li key={t.key} className="text-[12.5px] leading-[1.45] text-ink-2 text-pretty">
              {t.text}
            </li>
          ))}
        </ul>
      )}
      <p className="text-[12px] leading-[1.45] text-ink-3">
        {reminders.error ? (
          errorText(reminders.error, 'Reminders could not be loaded.')
        ) : reminders.data ? (
          <>
            {openReminders} open {openReminders === 1 ? 'reminder' : 'reminders'} ·{' '}
            <Link to="/tasks" className="font-medium text-ink-2 underline underline-offset-2">
              Tasks
            </Link>
          </>
        ) : (
          'Reminders…'
        )}
      </p>
    </Panel>
  )
}
